import React, { useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_ITEM,
  LINE_ITEM_HOUR,
  CATEGORY_GOWNS,
  CATEGORY_BRIDAL_GOWNS,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import {
  ModalInMobile,
  PrimaryButton,
  AvatarSmall,
  H1,
  H2,
  InlineTextButton,
  IconCollection,
} from '../../components';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './OrderPanel.module.css';
import { ExpandableBio } from '../../containers/ListingPage/UserCard/UserCard';
const { Money } = sdkTypes;

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price?.amount) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <FormattedMessage id="OrderPanel.askingPrice" />
      <p className={css.price}>{formatMoney(intl, price)}</p>
      <div className={css.perUnit}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    makeAnOfferButton,
    addToBagAndRemoveFromBag,
  } = props;

  const [tab, setTab] = useState('details');

  const publicData = listing?.attributes?.publicData || {};
  const {
    unitType,
    transactionProcessAlias = '',
    originalPrice,
    brand,
    productType,
    category,
    size,
    shoeSize,
    streetSize,
    style,
    material,
    color,
    anyDamage,
    anyBlemishesOrDamage,
    purchasedYear,
    yearPurchased,
    originalOwner,
    termsOfSale,
    originalReceipt,
    allowNegotiation,
    markForSale,
    prices,
    sleeves,
    neckline,
    heelHeight,
    length,
    designer,
    alterations
  } = publicData || {};

  const { isOriginalOwner, isInvoiceAvailable, allowOffers } = termsOfSale || {};
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const newPrice = new Money(price?.amount, price?.currency);
  const description = listing?.attributes?.description;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const showPriceMissing = isPaymentProcess && !newPrice;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const showInvalidCurrency = isPaymentProcess && newPrice?.currency !== marketplaceCurrency;
  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm = (isPurchase && typeof currentStock === 'number');

  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const showClosedListingHelpText = listing.id && isClosed;
  const isOrderOpen = !!parse(location.search).orderOpen;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  const handleContactUserClick = () => {
    onContactUser(author);
  };
  const contact = !showInquiryForm ? (
    <InlineTextButton
      rootClassName={css.contact}
      onClick={handleContactUserClick}
      disabled={isOwnListing}
      enforcePagePreloadFor="SignupPage"
    >
      <IconCollection icon="contactUserIcon" />
      <FormattedMessage id="UserCard.contactUser" />
    </InlineTextButton>
  ) : null;

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        className={css.modalMobile}
      >
        <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div>

        <div className={css.orderHeading}>
          {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
          {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
        </div>
        <div className={css.orginalPrice}>
          <FormattedMessage id="OrderPanel.originalPrice" />
          {(originalPrice || prices?.retailPrice / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        </div>
        <PriceMaybe
          price={newPrice}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
        />

        <div className={css.author}>
          <AvatarSmall user={author} className={css.providerAvatar} />
          <span className={css.providerNameLinked}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
            <span className={css.contactBtn}>{contact}</span>
          </span>
        </div>
        <ExpandableBio className={css.description} bio={description} />
        <div className={css.buttonGroups}>
          {makeAnOfferButton}
          {addToBagAndRemoveFromBag}
        </div>
        {showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={onSubmit}
            price={newPrice}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
          />
        ) : showBookingDatesForm ? (
          <BookingDatesForm
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={onSubmit}
            price={newPrice}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
          />
        ) : showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            onSubmit={onSubmit}
            markForSale={markForSale}
            price={newPrice}
            marketplaceCurrency={marketplaceCurrency}
            currentStock={currentStock}
            pickupEnabled={pickupEnabled}
            shippingEnabled={shippingEnabled}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onContactUser={onContactUser}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            listing={listing}
          />
        ) : showInquiryForm ? (
          <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={onSubmit} />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
      </ModalInMobile>
      <H2 className={css.productDetailsTitle}>
        <FormattedMessage id="EditListingWizard.tabLabelProductDetails" />
      </H2>
      <div>

        <div>
          <div className={css.productDetails}>
            {(brand && typeof (brand) == "string") ? (
              <b className={classNames(css.label, css.noUnderlineLabel)}>
                <FormattedMessage
                  id="OrderPanel.brand"
                  values={{ brand: <span className={css.value} >{brand}</span> }}
                />
              </b>
            ) : null}
            {(size || shoeSize || size == 0 && productType == CATEGORY_GOWNS) ? (
              <b className={css.label}>
                <FormattedMessage
                  id={[CATEGORY_GOWNS, CATEGORY_BRIDAL_GOWNS].includes(productType) ? "EditListingPreviewPanel.bridalSize" : "EditListingPreviewPanel.size"}
                  values={{ size: <span className={css.value} >{size || shoeSize || 0}</span> }}
                />
              </b>
            ) : null}

            {(color && typeof (color) == "string") ? (
              <b className={css.label}>
                <FormattedMessage
                  id="OrderPanel.color"
                  values={{ color: <span className={css.value} >{color}</span> }}
                />
              </b>
            ) : null}

            {(streetSize || streetSize == 0 && productType == CATEGORY_GOWNS) ? (
              <b className={css.label}>
                <FormattedMessage
                  id="OrderPanel.streetSize"
                  values={{ streetSize: <span className={css.value} >{streetSize || streetSize || 0}</span> }}
                />
              </b>
            ) : null}

            {heelHeight ? (
              <b className={classNames(css.label, css.itemType)}>
                <FormattedMessage
                  id="OrderPanel.heelHeight"
                  values={{ heelHeight: <span className={css.value} >{heelHeight}</span> }}
                />
              </b>
            ) : null}
            {(designer && typeof (designer) == "string") ? (
              <b className={css.label}>
                <FormattedMessage
                  id="OrderPanel.designer"
                  values={{ designer: <span className={css.value} >{designer}</span> }}
                />
              </b>
            ) : null}

            {(neckline && typeof (neckline) == "string") ? (
              <b className={classNames(css.label, css.itemType)}>
                <FormattedMessage
                  id="OrderPanel.neckline"
                  values={{ neckline: <span className={css.value} >{neckline}</span> }}
                />
              </b>
            ) : null}

            {(length && typeof (length) == "string") ? (
              <b className={classNames(css.label, css.itemType)}>
                <FormattedMessage
                  id="OrderPanel.length"
                  values={{ length: <span className={css.value} >{length}</span> }}
                />
              </b>
            ) : null}

            {(sleeves && typeof (sleeves) == "string") ? (
              <b className={classNames(css.label, css.itemType)}>
                <FormattedMessage
                  id="OrderPanel.sleeves"
                  values={{ sleeves: <span className={css.value} >{sleeves}</span> }}
                />
              </b>
            ) : null}

            {(material && typeof (material) == "string") ? (
              <b className={css.label}>
                <FormattedMessage
                  id="OrderPanel.material"
                  values={{ material: <span className={css.value} >{material}</span> }}
                />
              </b>
            ) : null}

            {(Array.isArray(style) && style?.length > 0) ? (
              <b className={css.label}>
                <FormattedMessage
                  id="EditListingPreviewPanel.styleArray"
                />
                {style.map((e, index) => (
                  <span className={css.value} key={index}>
                    {e}{index !== style.length - 1 ? ', ' : ''}
                  </span>
                ))} </b>)
              : style ? (
                <b className={css.label}>
                  <FormattedMessage
                    id="OrderPanel.style"
                    values={{ style: <span className={css.value} >{style}</span> }}
                  />
                </b>
              ) : null}

            {
              (purchasedYear || yearPurchased) ? (<b className={css.label}>
                <FormattedMessage
                  id="OrderPanel.purchasedYear"
                  values={{ purchasedYear: <span className={css.value} >{purchasedYear || yearPurchased}</span> }}
                />
              </b>) : null
            }

            {
              originalOwner ? (<b className={css.label}>
                <FormattedMessage
                  id="OrderPanel.originalOwner"
                  values={{ originalOwner: <span className={css.value} >{(originalOwner || isOriginalOwner)?.label}</span> }}
                />
              </b>) : null
            }

            {
              originalReceipt ? (<b className={css.label}>
                <FormattedMessage
                  id="OrderPanel.originalReceiptAvailable"
                  values={{ originalReceiptAvailable: <span className={css.value} >{(originalReceipt || isInvoiceAvailable)?.label}</span> }}
                />
              </b>) : null
            }

            <b className={css.label}>
              <FormattedMessage
                id="OrderPanel.negotiation"
                values={{ negotiation: <span className={css.value} >{allowNegotiation ? allowNegotiation?.charAt(0).toUpperCase() + allowNegotiation?.slice(1) : allowOffers ? "Allowed" : "No"} </span> }}
              />
            </b>

            {(alterations)?.trim() ? (<b className={classNames(css.label, css.noUnderlineLabel)}>
              <FormattedMessage
                id="EditListingPreviewPanel.alterations"
                values={{ alterations: <span className={css.value} >{alterations}</span> }}
              />
            </b>) : null}

            {(anyDamage || anyBlemishesOrDamage)?.trim() ? (<b className={classNames(css.label, css.noUnderlineLabel)}>
              <FormattedMessage
                id="OrderPanel.anyDamage"
                values={{ anyDamage: <span className={css.value} >{anyDamage || anyBlemishesOrDamage}</span> }}
              />
            </b>) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withRouter, injectIntl)(OrderPanel);
